import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaTimes, FaSave, FaExpand, FaCompress } from 'react-icons/fa';
import { Keyboard } from '@capacitor/keyboard';
import { Capacitor } from '@capacitor/core';
import { isCapacitor } from '../../utils/platform';
import './FullScreenTextEditor.css';
import { createLogger } from '../../services/Debug';
const FullScreenTextEditor = ({
  isOpen,
  onClose,
  onSave,
  initialValue = '',
  title = 'Modifier le texte',
  placeholder = 'Entrez votre texte ici...',
  maxLength = null,
  showCharCount = true,
  loading = false,
  saveButtonText = 'Enregistrer',
  cancelButtonText = 'Annuler',
  allowMarkdown = false,
  autofocus = true,
  keyboardHeight = 0, // This will be passed from parent hook
  isIOS = false // This will be passed from parent hook
}) => {
  const log = createLogger('FullScreenTextEditor');
  const [value, setValue] = useState(initialValue);
  const [isDirty, setIsDirty] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [localKeyboardHeight, setLocalKeyboardHeight] = useState(0);
  const textareaRef = useRef(null);
  const containerRef = useRef(null);
  const lastSavedValue = useRef(initialValue);
  const isIOSDevice = useRef(isIOS || (Capacitor.getPlatform() === 'ios'));

  // Use either passed keyboard height or local state
  const effectiveKeyboardHeight = keyboardHeight || localKeyboardHeight;

  // Initialize value when modal opens
  useEffect(() => {
    if (isOpen) {
      setValue(initialValue);
      lastSavedValue.current = initialValue;
      setIsDirty(false);
      setIsFullscreen(false);
      
      // Setup keyboard for iOS
      if (isCapacitor && isIOSDevice.current) {
        // Configure keyboard settings
        Keyboard.setAccessoryBarVisible({ isVisible: false });
        Keyboard.setResizeMode({ mode: 'none' });
        Keyboard.setScroll({ isDisabled: true });
      }
      
      // Focus textarea after a small delay
      if (autofocus) {
        setTimeout(() => {
          textareaRef.current?.focus();
          // Move cursor to end of text
          if (textareaRef.current) {
            const length = textareaRef.current.value.length;
            textareaRef.current.setSelectionRange(length, length);
          }
        }, 300); // Increased delay for iOS
      }
    }
  }, [isOpen, initialValue, autofocus]);

  // Local keyboard event handling if parent doesn't provide keyboard height
  useEffect(() => {
    if (!isOpen || !isCapacitor || keyboardHeight !== undefined) return;

    let showListener;
    let hideListener;

    const setupKeyboardListeners = async () => {
      if (isIOSDevice.current) {
        showListener = await Keyboard.addListener('keyboardWillShow', (info) => {
          setLocalKeyboardHeight(info.keyboardHeight);
        });

        hideListener = await Keyboard.addListener('keyboardWillHide', () => {
          setLocalKeyboardHeight(0);
        });
      } else {
        // Android
        showListener = await Keyboard.addListener('keyboardDidShow', (info) => {
          setLocalKeyboardHeight(info.keyboardHeight);
        });

        hideListener = await Keyboard.addListener('keyboardDidHide', () => {
          setLocalKeyboardHeight(0);
        });
      }
    };

    setupKeyboardListeners();

    return () => {
      if (showListener) showListener.remove();
      if (hideListener) hideListener.remove();
    };
  }, [isOpen, keyboardHeight]);

  // Handle value changes
  const handleChange = (e) => {
    const newValue = e.target.value;
    
    // Enforce max length if specified
    if (maxLength && newValue.length > maxLength) {
      return;
    }
    
    setValue(newValue);
    setIsDirty(newValue !== lastSavedValue.current);
  };

  // Handle save
  const handleSave = async () => {
    if (loading) return;
    
    try {
      await onSave(value);
      lastSavedValue.current = value;
      setIsDirty(false);
      // Hide keyboard before closing on iOS
      if (isCapacitor && isIOSDevice.current) {
        await Keyboard.hide();
      }
      onClose();
    } catch (error) {
      log.error('Error saving:', error);
      // Error handling is done by the parent component
    }
  };

  // Handle close with confirmation if dirty
  const handleClose = () => {
    if (isDirty) {
      const confirm = window.confirm(
        'Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter sans enregistrer ?'
      );
      if (!confirm) return;
    }
    
    // Hide keyboard before closing on iOS
    if (isCapacitor && isIOSDevice.current) {
      Keyboard.hide();
    }
    
    onClose();
  };

  // Keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      // Escape to close
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      }
      // Ctrl/Cmd + Enter to save
      else if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        handleSave();
      }
      // Ctrl/Cmd + S to save
      else if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isDirty, value, loading]);

  // Handle fullscreen toggle (web only)
  const toggleFullscreen = () => {
    if (!document.fullscreenEnabled || isCapacitor) return;
    
    if (!isFullscreen) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Auto-save to localStorage (draft)
  useEffect(() => {
    if (!isOpen || !isDirty) return;
    
    const timeoutId = setTimeout(() => {
      const draftKey = `draft_${title.replace(/\s+/g, '_')}`;
      localStorage.setItem(draftKey, value);
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [value, isDirty, isOpen, title]);

  // Load draft on mount if exists
  useEffect(() => {
    if (isOpen && !initialValue) {
      const draftKey = `draft_${title.replace(/\s+/g, '_')}`;
      const draft = localStorage.getItem(draftKey);
      if (draft) {
        const useDraft = window.confirm(
          'Un brouillon a été trouvé. Voulez-vous le restaurer ?'
        );
        if (useDraft) {
          setValue(draft);
          setIsDirty(true);
        } else {
          localStorage.removeItem(draftKey);
        }
      }
    }
  }, [isOpen, initialValue, title]);

  // Clear draft on successful save
  const clearDraft = () => {
    const draftKey = `draft_${title.replace(/\s+/g, '_')}`;
    localStorage.removeItem(draftKey);
  };

  // Touch handling for mobile swipe to close
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientY);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientY);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isDownSwipe = distance < -minSwipeDistance;
    
    if (isDownSwipe && touchStart < 100) { // Started from top
      handleClose();
    }
  };

  if (!isOpen) return null;

  const charCount = value.length;
  const charRemaining = maxLength ? maxLength - charCount : null;
  const charPercentage = maxLength ? (charCount / maxLength) * 100 : 0;

  // Calculate container styles based on keyboard
  const containerStyle = {
    height: effectiveKeyboardHeight > 0 ? `calc(100% - ${effectiveKeyboardHeight}px)` : '100%',
    transition: 'height 0.3s ease-in-out'
  };

  return (
    <div className={`fullscreen-editor-overlay ${isCapacitor ? 'mobile' : 'desktop'} ${effectiveKeyboardHeight > 0 ? 'keyboard-visible' : ''}`}>
      <div 
        ref={containerRef}
        className="fullscreen-editor-container"
        style={containerStyle}
        onTouchStart={isCapacitor ? onTouchStart : undefined}
        onTouchMove={isCapacitor ? onTouchMove : undefined}
        onTouchEnd={isCapacitor ? onTouchEnd : undefined}
      >
        {/* Header */}
        <div className="fullscreen-editor-header">
          <button 
            className="fullscreen-editor-close"
            onClick={handleClose}
            title="Fermer (Esc)"
            style={isCapacitor ? { position: 'absolute', left: '8px', top: '8px', zIndex: 20 } : {}}
          >
            <FaTimes />
          </button>
          <h2 className="fullscreen-editor-title" style={isCapacitor ? { display: 'none' } : {}}>{title}</h2>
          {!isCapacitor && (
            <button 
              className="fullscreen-editor-fullscreen"
              onClick={toggleFullscreen}
              title={isFullscreen ? 'Quitter plein écran' : 'Plein écran'}
            >
              {isFullscreen ? <FaCompress /> : <FaExpand />}
            </button>
          )}
        </div>

        {/* Mobile swipe indicator - removed for more space */}

        {/* Editor */}
        <div className="fullscreen-editor-body">
          <textarea
            ref={textareaRef}
            className="fullscreen-editor-textarea"
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            spellCheck="true"
            autoComplete="off"
            maxLength={maxLength || undefined}
          />
          
          {allowMarkdown && (
            <div className="fullscreen-editor-hint">
              Astuce : Vous pouvez utiliser du Markdown pour formater votre texte
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="fullscreen-editor-footer">
          <div className="fullscreen-editor-info">
            {showCharCount && (
              <div className="fullscreen-editor-char-count">
                {maxLength ? (
                  <>
                    <span className={charRemaining < 100 ? 'warning' : ''}>
                      {charCount} / {maxLength}
                    </span>
                    {charRemaining < 100 && (
                      <span className="char-remaining">
                        ({charRemaining} restants)
                      </span>
                    )}
                  </>
                ) : (
                  <span>{charCount} caractères</span>
                )}
              </div>
            )}
            {maxLength && (
              <div className="fullscreen-editor-progress">
                <div 
                  className="fullscreen-editor-progress-bar"
                  style={{ 
                    width: `${charPercentage}%`,
                    backgroundColor: charPercentage > 90 ? '#ff6b6b' : '#4CAF50'
                  }}
                />
              </div>
            )}
          </div>

          <div className="fullscreen-editor-actions">
            {isDirty && (
              <span className="fullscreen-editor-unsaved">
                Modifications non enregistrées
              </span>
            )}
            <button 
              className="fullscreen-editor-btn fullscreen-editor-btn-cancel"
              onClick={handleClose}
              disabled={loading}
            >
              {cancelButtonText}
            </button>
            <button 
              className="fullscreen-editor-btn fullscreen-editor-btn-save"
              onClick={() => {
                handleSave();
                clearDraft();
              }}
              disabled={loading || (!isDirty && value === initialValue)}
            >
              <FaSave />
              {loading ? 'Enregistrement...' : saveButtonText}
            </button>
          </div>
        </div>

        {/* Keyboard shortcuts hint */}
        {!isCapacitor && (
          <div className="fullscreen-editor-shortcuts">
            <span>Ctrl+Enter ou Ctrl+S pour enregistrer</span>
            <span>•</span>
            <span>Esc pour fermer</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default FullScreenTextEditor;